import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

/**
 * Story 67.1 — Admin page for Voice Agent Script CRUD.
 * Also renders the A/B Test list under a tab (Story 67.3).
 *
 * Route: /admin/voice-scripts
 */

interface VoiceScript {
  id: string;
  name: string;
  version: number;
  isActive: boolean;
  dsl: string;
  createdAt: string;
}

interface AbTest {
  id: string;
  name: string;
  variantAId: string;
  variantBId: string;
  trafficSplit: number;
  winner: string | null;
  concludedAt: string | null;
  startedAt: string;
}

type Tab = 'scripts' | 'abTests';

const EMPTY_DSL = JSON.stringify(
  {
    nodes: [
      { id: 'n1', type: 'say', text: 'Hola, ¿tiene un momento?' },
      { id: 'n2', type: 'end', outcome: 'COMPLETED' },
    ],
    edges: [{ from: 'n1', to: 'n2' }],
    startNodeId: 'n1',
  },
  null,
  2,
);

export function VoiceScriptsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('scripts');

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('voice.scripts.title')}</h1>
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setTab('scripts')}
          className={`px-3 py-1 text-sm rounded-t ${tab === 'scripts' ? 'font-medium border-b-2 border-primary' : 'text-muted-foreground'}`}
        >
          {t('voice.scripts.tabs.list')}
        </button>
        <button
          onClick={() => setTab('abTests')}
          className={`px-3 py-1 text-sm rounded-t ${tab === 'abTests' ? 'font-medium border-b-2 border-primary' : 'text-muted-foreground'}`}
        >
          {t('voice.scripts.tabs.abTests')}
        </button>
      </div>
      {tab === 'scripts' ? <ScriptsTab /> : <AbTestsTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scripts tab
// ---------------------------------------------------------------------------

function ScriptsTab() {
  const { t } = useTranslation();
  const [scripts, setScripts] = useState<VoiceScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    apiClient
      .get<VoiceScript[]>('/voice/scripts')
      .then((r) => setScripts(r.data))
      .catch((err) => setError(isAxiosError(err) ? err.message : String(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  function handleDelete(id: string) {
    if (!window.confirm(t('voice.scripts.confirmDelete'))) return;
    apiClient.delete(`/voice/scripts/${id}`).then(load);
  }

  if (loading) return <div className="text-muted-foreground text-sm">{t('app.loading', 'Loading...')}</div>;
  if (error) return <div className="text-destructive text-sm">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setShowNew(true); setEditId(null); }}>
          <Plus className="h-4 w-4 mr-1" />
          {t('voice.scripts.new')}
        </Button>
      </div>

      {(showNew || editId) && (
        <ScriptForm
          scriptId={editId}
          onSaved={() => { setShowNew(false); setEditId(null); load(); }}
          onCancel={() => { setShowNew(false); setEditId(null); }}
        />
      )}

      {scripts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('voice.scripts.empty')}</p>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-4 py-2">{t('voice.scripts.name')}</th>
                <th className="px-4 py-2">{t('voice.scripts.version')}</th>
                <th className="px-4 py-2">{t('voice.scripts.active')}</th>
                <th className="px-4 py-2">{t('voice.scripts.created')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {scripts.map((s) => (
                <tr key={s.id} className="border-t hover:bg-muted/30">
                  <td className="px-4 py-2 font-medium">{s.name}</td>
                  <td className="px-4 py-2">{s.version}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${s.isActive ? 'bg-emerald-900/40 text-emerald-200' : 'bg-muted text-muted-foreground'}`}>
                      {s.isActive ? t('voice.scripts.active') : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2 flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => { setEditId(s.id); setShowNew(false); }}>
                      {t('voice.scripts.edit')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(s.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function ScriptForm({
  scriptId,
  onSaved,
  onCancel,
}: {
  scriptId: string | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [dsl, setDsl] = useState(EMPTY_DSL);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (scriptId) {
      apiClient.get<VoiceScript>(`/voice/scripts/${scriptId}`).then((r) => {
        setName(r.data.name);
        setIsActive(r.data.isActive);
        try {
          setDsl(JSON.stringify(JSON.parse(r.data.dsl), null, 2));
        } catch {
          setDsl(r.data.dsl);
        }
      });
    }
  }, [scriptId]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    let parsedDsl: unknown;
    try {
      parsedDsl = JSON.parse(dsl);
    } catch {
      setError('Invalid JSON in DSL field.');
      setSaving(false);
      return;
    }

    const req = scriptId
      ? apiClient.put(`/voice/scripts/${scriptId}`, { name, dsl: parsedDsl, isActive })
      : apiClient.post('/voice/scripts', { name, dsl: parsedDsl });

    req
      .then(onSaved)
      .catch((err) => setError(isAxiosError(err) ? err.message : String(err)))
      .finally(() => setSaving(false));
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium">{scriptId ? t('voice.scripts.edit') : t('voice.scripts.new')}</h2>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium mb-1">{t('voice.scripts.name')}</label>
          <input
            className="w-full border rounded px-3 py-1.5 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t('voice.scripts.dsl')}</label>
          <textarea
            className="w-full border rounded px-3 py-2 text-xs font-mono h-48"
            value={dsl}
            onChange={(e) => setDsl(e.target.value)}
            spellCheck={false}
          />
        </div>
        {scriptId && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            {t('voice.scripts.active')}
          </label>
        )}
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={saving}>{t('voice.scripts.save')}</Button>
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>{t('app.cancel', 'Cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// A/B Tests tab
// ---------------------------------------------------------------------------

function AbTestsTab() {
  const { t } = useTranslation();
  const [tests, setTests] = useState<AbTest[]>([]);
  const [scripts, setScripts] = useState<VoiceScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<Record<string, unknown> | null>(null);

  function load() {
    Promise.all([
      apiClient.get<AbTest[]>('/voice/script-tests'),
      apiClient.get<VoiceScript[]>('/voice/scripts'),
    ])
      .then(([t, s]) => { setTests(t.data); setScripts(s.data); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  function viewAnalytics(id: string) {
    setSelectedId(id);
    apiClient.get<Record<string, unknown>>(`/voice/script-tests/${id}/analytics`)
      .then((r) => setAnalytics(r.data));
  }

  if (loading) return <div className="text-muted-foreground text-sm">{t('app.loading', 'Loading...')}</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowNew(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t('voice.scripts.abTest.new')}
        </Button>
      </div>

      {showNew && (
        <AbTestForm
          scripts={scripts}
          onSaved={() => { setShowNew(false); load(); }}
          onCancel={() => setShowNew(false)}
        />
      )}

      {selectedId && analytics && (
        <Card className="p-4 space-y-2">
          <div className="flex justify-between items-center">
            <h3 className="font-medium">{t('voice.scripts.abTest.analytics')}</h3>
            <button className="text-xs text-muted-foreground" onClick={() => { setSelectedId(null); setAnalytics(null); }}>✕</button>
          </div>
          <pre className="text-xs font-mono overflow-auto">{JSON.stringify(analytics, null, 2)}</pre>
        </Card>
      )}

      {tests.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('voice.scripts.abTest.empty')}</p>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-4 py-2">{t('voice.scripts.abTest.name')}</th>
                <th className="px-4 py-2">{t('voice.scripts.abTest.split')}</th>
                <th className="px-4 py-2">{t('voice.scripts.abTest.status')}</th>
                <th className="px-4 py-2">{t('voice.scripts.abTest.winner')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {tests.map((test) => (
                <tr key={test.id} className="border-t hover:bg-muted/30">
                  <td className="px-4 py-2 font-medium">{test.name}</td>
                  <td className="px-4 py-2">{test.trafficSplit}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${test.concludedAt ? 'bg-muted text-muted-foreground' : 'bg-amber-900/40 text-amber-200'}`}>
                      {test.concludedAt ? t('voice.scripts.abTest.concluded') : t('voice.scripts.abTest.running')}
                    </span>
                  </td>
                  <td className="px-4 py-2">{test.winner ?? '—'}</td>
                  <td className="px-4 py-2 flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => viewAnalytics(test.id)}>
                      {t('voice.scripts.abTest.analytics')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function AbTestForm({
  scripts,
  onSaved,
  onCancel,
}: {
  scripts: VoiceScript[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [variantAId, setVariantAId] = useState('');
  const [variantBId, setVariantBId] = useState('');
  const [split, setSplit] = useState('0.5');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    apiClient
      .post('/voice/script-tests', {
        name,
        variantAId,
        variantBId,
        trafficSplit: parseFloat(split),
      })
      .then(onSaved)
      .catch((err) => setError(isAxiosError(err) ? err.message : String(err)))
      .finally(() => setSaving(false));
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium">{t('voice.scripts.abTest.new')}</h2>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium mb-1">{t('voice.scripts.abTest.name')}</label>
          <input className="w-full border rounded px-3 py-1.5 text-sm" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">{t('voice.scripts.abTest.variantA')}</label>
            <select className="w-full border rounded px-3 py-1.5 text-sm" value={variantAId} onChange={(e) => setVariantAId(e.target.value)} required>
              <option value="">—</option>
              {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('voice.scripts.abTest.variantB')}</label>
            <select className="w-full border rounded px-3 py-1.5 text-sm" value={variantBId} onChange={(e) => setVariantBId(e.target.value)} required>
              <option value="">—</option>
              {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t('voice.scripts.abTest.split')}</label>
          <input className="w-full border rounded px-3 py-1.5 text-sm" type="number" min="0" max="1" step="0.01" value={split} onChange={(e) => setSplit(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={saving}>{t('voice.scripts.save')}</Button>
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>{t('app.cancel', 'Cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
